import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../Config/Api";
import { AppType } from "../../types/appType";
import { AuthType } from "../../types/authType";
import { ErrorType } from "../../types/errorType";
import { RootState } from "../store";
import { IDSorganisation } from "types/idsOrganisation";
import { ExtractErrorInfo } from "../../components/shared/ExtractErrorInfo";

export interface AppTypeWithAuthType extends AppType {
  authType: AuthType;
}

const initialState: AppTypeWithAuthType = {
  error: null,
  loading: false,
  authType: {
    isAuthenticated: false,
    access_token: "",
    expires_in: null,
    refresh_token: null,
    scope: null,
    token_type: null,
    issuer: null,
  },
};

export const getAccessToken = createAsyncThunk<AuthType, any>(
  "auth/GetAccessToken",
  async (requestBody, { rejectWithValue }) => {
    try {
      const storedIssuer = localStorage.getItem("idsIssuer");
      //TODO CHANGE - Log for aravind
      console.log(storedIssuer, "Stored Issuer")
      const response = await api.post<AuthType>(storedIssuer + "/connect/token", requestBody, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return response.data;
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

/**
 * Gets user organistaion information from Identity server.
 */
export const validateTokenAndGetUserInfo = createAsyncThunk<IDSorganisation>(
  "auth/validateTokenAndGetUserInfo",
  async (_, { rejectWithValue }) => {
    try {
      const storedIssuer = localStorage.getItem("idsIssuer");
      const response = await api.get<IDSorganisation>(`${storedIssuer}/connect/userinfo`);

      return response.data;
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearLocalStorageToken: (state) => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("idsIssuer");
      state.authType.isAuthenticated = false;
      state.authType.access_token = null;
      state.authType.refresh_token = null;
      state.authType.issuer = null;
    },
    setAuthState: (state, action: PayloadAction<{ access_token: string; refresh_token: string }>) => {
      // Save access token to local storage
      localStorage.setItem("access_token", action.payload.access_token);
      localStorage.setItem("refresh_token", action.payload.refresh_token);

      return {
        ...state,
        authType: {
          ...state.authType,
          isAuthenticated: true,
          access_token: action.payload.access_token,
          refresh_token: action.payload.refresh_token,
        },
      };
    },
    setAuthError: (state, action: PayloadAction<ErrorType>) => {
      state.error = action.payload;
      return state;
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    setAuthIssuer: (state, action: PayloadAction<string>) => {
      localStorage.setItem("idsIssuer", action.payload);
      state.authType.issuer = action.payload;
    },
    setAuthReferrer: (state, action: PayloadAction<string>) => {
      localStorage.setItem("referrerURL", action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAccessToken.fulfilled, (state, action: PayloadAction<AuthType>) => {
      // Save access token to local storage
      localStorage.setItem("access_token", action.payload.access_token as string);
      localStorage.setItem("refresh_token", action.payload.refresh_token as string);

      state.authType.isAuthenticated = true;
      state.authType.access_token = action.payload.access_token;
      state.authType.expires_in = action.payload.expires_in;
      state.authType.refresh_token = action.payload.refresh_token;
      state.authType.scope = action.payload.scope;
      state.loading = false;
    });
    builder.addCase(getAccessToken.pending, (state, action) => {
      state.loading = true;
    });
    //TODO - will need to pass the Type in, for now use any
    builder.addCase(getAccessToken.rejected, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload;
    });
    //TODO - will need to pass the UserType in, for now use any
    builder.addCase(validateTokenAndGetUserInfo.fulfilled, (state, action) => {
      state.authType.isAuthenticated = true;
      state.loading = false;
    });
    builder.addCase(validateTokenAndGetUserInfo.pending, (state, action) => {
      state.loading = true;
    });
    //TODO - will need to pass the Type in, for now use any
    builder.addCase(validateTokenAndGetUserInfo.rejected, (state, action: PayloadAction<any>) => {
      //Remove token from local storage has its failed to validate.
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      state.error = action.payload;
      state.authType.isAuthenticated = false;
      state.loading = false;
    });
  },
});

export const { clearLocalStorageToken, setAuthState, setAuthError, clearAuthError, setAuthIssuer, setAuthReferrer } =
  authSlice.actions;
export const actionRootState = (state: RootState) => state;
export default authSlice.reducer;
